<template>
  <section>
    <div>
      <h4 :class="[$style.propertyHeader, $style.headerBottomMargin]">
        Location
      </h4>

      <!-- Resources section -->
      <div :class="$style.flexSingleWidth">
        <InputValidator
          :config="inputsConfig.resourcesPathConfig"
          data-test-id="IdmCustomListUsers_resourcesPath"
          @InputValidatorChange="(value) => handleEndpointChange(value, 'resourcesPath')"
        />
        <p :class="$style.supportingText">
          Please provide a JSON path to your user resource object.
        </p>
      </div>

      <!-- Endpoint path -->
      <div
        :class="$style.methodAndUrl"
      >
        <div :class="$style.selectWrapper">
          <Select
            data-test-id="IdmCustomListUsers_listUserMethod"
            :isReadonly="true"
            isDisabled
            label="Method *"
            :options="verbOpts"
            :value="listUsers.verb"
          />
        </div>
        <InputValidator
          :config="inputsConfig.listUsersConfig"
          data-test-id="IdmCustomListUsers_listUserPath"
          @InputValidatorChange="(value) => handleEndpointChange(value, 'path')"
        />
      </div>
    </div>

    <!-- Total Count -->
    <div>
      <h4 :class="[$style.propertyHeader, $style.headerBottomMargin]">
        Total count
      </h4>
      <p>
        Specify the name and location for the total count parameter for this API.
        Total count is a positive numeric value that defines the number of total
        items returned from the API call. The value for this attribute is used to
        determine when we have reached the last page of results.
      </p>
      <div :class="[$style.flexContainer, $style.inputRow]">
        <div :class="$style.flexSingleWidth">
          <DropDown
            data-test-id="IdmCustomListUsers_parameterLocation"
            :disabled="false"
            label="Response parameter location *"
            name="options"
            :options="parameterOptions.options"
            :value="parameterOptions.selected"
            @input="handleParameterLocationChange"
          />
        </div>
        <div :class="$style.flexSingleWidth">
          <InputValidator
            :config="inputsConfig.parameterOptionsConfig"
            data-test-id="IdmCustomListUsers_parameter"
            @InputValidatorChange="(value) => handleEndpointChange(value, 'totalCount.value')"
          />
          <p
            v-show="totalCountConfig.showSupportingText"
            :class="[
              $style.supportingText,
            ]"
          >
            Please provide a JSON path to your Total Count object.
          </p>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div>
      <h4 :class="[$style.propertyHeader, $style.headerBottomMargin]">
        Pagination
      </h4>
      <p>
        Specify the name of the parameters used for paginating results from calls to this API.
        Pagination allows a large number of results to be returned in more manageable sizes,
        referred to as pages. There are two parameters, typically named limit and offset,
        that allow you to control how many results are returned per page (limit) and on which
        record to start (offset).
      </p>
      <div :class="[$style.flexContainer, $style.inputRow]">
        <div
          :class="$style.flexSingleWidth"
        >
          <InputValidator
            :config="inputsConfig.limitConfig"
            data-test-id="IdmCustomListUsers_limit"
            @InputValidatorChange="(value) => handleEndpointChange(value, storeLimitPath)"
          />
        </div>
        <div
          :class="$style.flexSingleWidth"
        >
          <InputValidator
            :config="inputsConfig.skipConfig"
            data-test-id="IdmCustomListUsers_skip"
            @InputValidatorChange="(value) => handleEndpointChange(value, storeSkipPath)"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { ItemTypeConsts, StoreModelMap } from 'client/util/Constants';
import { Select } from '@jumpcloud/ui-components';
import { cloneDeep, set } from 'lodash';
import {
  isEndpoint,
  isHTTPHeader,
  isJSONPath,
  isSecureUrl,
} from 'client/applications/utils/ApplicationUtils';
import { mapActions, mapState } from 'vuex';
import {
  parameterLocationOptions, parameterOptionLabels, requiredValidatedFields, verbs,
} from 'client/util/Constants/ApplicationConstants';
import DropDown from 'client/components/FormValidation/DropDown.vue';
import InputValidator from 'client/components/FormValidation/InputValidator.vue';

const { application } = ItemTypeConsts;
const ApplicationModel = StoreModelMap[application];
const { listUsersEndpointPath } = requiredValidatedFields;

export default {
  name: 'ListUsersEndpointConfig',
  components: {
    DropDown,
    InputValidator,
    Select,
  },

  inject: ['$validator'],

  data() {
    const { customIdmEndpointsConfig: endpointsConfig } = this.$store.state[ApplicationModel];
    const totalCount = this.getTotalCountConfig(endpointsConfig.listUsers.totalCount.location);

    return {
      totalCountConfig: {
        label: totalCount.label,
        placeholder: totalCount.placeholder,
        validate: totalCount.validate,
        showSupportingText: totalCount.showSupportingText,
      },
      verbOpts: Object.keys(verbs).map(key => ({ name: key, value: key })),
      storeSkipPath: 'pagination.skip',
      storeLimitPath: 'pagination.limit',
    };
  },

  computed: {
    ...mapState(ApplicationModel, {
      endpointsConfig: 'customIdmEndpointsConfig',
    }),

    inputsConfig() {
      const { totalCountConfig } = this;
      return {
        resourcesPathConfig: {
          label: 'Resource Location *',
          name: 'Resource Location',
          placeholder: 'Example: results.list',
          type: 'text',
          validate: {
            isJSONPath: true,
            required: true,
          },
          value: this.listUsers.resourcesPath,
        },
        listUsersConfig: {
          label: 'Endpoint path *',
          name: listUsersEndpointPath,
          placeholder: '/users',
          type: 'text',
          validate: {
            required: true,
            isEndpoint: true,
          },
          value: this.listUsers.path,
        },
        parameterOptionsConfig: {
          label: totalCountConfig.label,
          name: 'Response Location',
          placeholder: totalCountConfig.placeholder,
          type: 'text',
          validate: totalCountConfig.validate,
          value: this.listUsers.totalCount.value,
        },
        limitConfig: {
          label: 'Limit name *',
          name: 'Limit name',
          validate: 'required',
          value: this.listUsers.pagination.limit,
        },
        skipConfig: {
          label: 'Offset name *',
          name: 'Offset name',
          validate: 'required',
          value: this.listUsers.pagination.skip,
        },
      };
    },

    listUsers() {
      return this.endpointsConfig.listUsers;
    },

    parameterOptions() {
      return {
        selected: this.listUsers.totalCount.location,
        options: Object.keys(parameterLocationOptions)
          .map(option => parameterLocationOptions[option]),
      };
    },
  },

  beforeMount() {
    isEndpoint(this.$validator);
    isHTTPHeader(this.$validator);
    isJSONPath(this.$validator);
    isSecureUrl(this.$validator);
  },

  methods: {
    ...mapActions(ApplicationModel, [
      'updateCustomIdmEndpoint',
    ]),

    getTotalCountConfig(location) {
      const config = {
        label: '',
        placeholder: '',
        validate: {},
        showSupportingText: true,
      };
      switch (location) {
        case parameterLocationOptions.BODY:
          config.label = parameterOptionLabels[parameterLocationOptions.BODY].LABEL;
          config.placeholder = parameterOptionLabels[parameterLocationOptions.BODY].PLACEHOLDER;
          config.validate = { isJSONPath: true, required: true };
          config.showSupportingText = true;
          break;

        case parameterLocationOptions.HEADER:
          config.label = parameterOptionLabels[parameterLocationOptions.HEADER].LABEL;
          config.placeholder = parameterOptionLabels[parameterLocationOptions.HEADER].PLACEHOLDER;
          config.validate = { isHTTPHeader: true, required: true };
          config.showSupportingText = false;
          break;
        default:
          break;
      }

      return config;
    },

    handleEndpointChange(newValue, path) {
      const endpointConfig = cloneDeep(this.endpointsConfig.listUsers);
      endpointConfig.func = 'listUsers';
      set(endpointConfig, path, newValue);
      this.updateCustomIdmEndpoint(endpointConfig);
    },

    handleParameterLocationChange(value) {
      const config = this.getTotalCountConfig(value);
      this.totalCountConfig = config;
      this.handleEndpointChange(value, 'totalCount.location');
    },
  },
};
</script>

<style module>
@import '../css/IdentityManagementCommon.css';

@value (jcBrand, jcGrey,  jcGrey200, jcGrey800) from '~css/brand-colors.css';

.selectWrapper {
  /*
  This class is to just align with the InputValidator,
  once we migrate to the new input component we may not need this
  */
  margin-top: 0.5rem;
}

.endpointConfigHeader {
  color: jcGrey800;
  font-weight: 600;
}

.headerWithDivision {
  border-bottom: 2px solid jcGrey200;
  color: jcGrey600;
  font-size: 1.2rem;
}

.headerBottomMargin {
  margin-bottom: 0.2rem;
}

.methodAndUrl {
  column-gap: 1rem;
  display: flex;
}

.propertyHeader {
  color: jcBrand;
}

.urlField {
  flex: 0 1 auto;
}

.verticalMargin {
  margin: 2rem 0;
}

.methodContainer {
  flex: 1 3 auto;
}

.visible {
  visibility: visible;
}

.hidden {
  visibility: hidden;
}
</style>
